import { SourcingChannelDetails } from 'api-clients/monolith';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PlaneSendIcon } from '@/components/icons/plane-send';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';

import { SelectChannelsDialog } from '../../job-posting/select-channels-dialog';
import { useChannelsStore } from '../use-channels-store';
import { Card } from './card';
import { useMessages } from './messages';

interface ChannelRecommendationProps {
  channels: SourcingChannelDetails[];
}

export function ChannelRecommendation({
  channels,
}: ChannelRecommendationProps) {
  const navigate = useNavigate();
  const { postJobPath } = useNavigationPaths();
  const { getChannelRecommendationMessages } = useMessages();
  const { header, description } = getChannelRecommendationMessages();
  const {
    setSelectedChannelSlugs,
    setSelectedOpeningIds,
    setOpenChannelsDialog,
    setRecommendedChannelSlugs,
  } = useJobPostingContext();
  const openingId = useChannelsStore(s => s.opening?.id);
  const handleSingleChannelPostJobClick = useCallback(() => {
    if (!openingId) return;

    setSelectedOpeningIds([openingId]);
    setSelectedChannelSlugs([channels[0].slug]);

    navigate(postJobPath);
  }, [
    channels,
    navigate,
    openingId,
    postJobPath,
    setSelectedChannelSlugs,
    setSelectedOpeningIds,
  ]);

  const handleMultipleChannelsPostJobClick = useCallback(() => {
    if (!openingId) return;

    setSelectedOpeningIds([openingId]);
    setSelectedChannelSlugs(channels.map(channel => channel.slug));
    setRecommendedChannelSlugs(channels.map(channel => channel.slug));
    setOpenChannelsDialog(true);
  }, [
    channels,
    openingId,
    setOpenChannelsDialog,
    setSelectedChannelSlugs,
    setSelectedOpeningIds,
    setRecommendedChannelSlugs,
  ]);

  // If there is a single channel and one of INDEED, SPRING, BOOST, show the channel name and a button to post the job
  // Otherwise show the channels in a list and a button to post the job to all the recommended channels
  return (
    <Card header={header} description={description}>
      {channels.length === 1 ? (
        <Button onClick={handleSingleChannelPostJobClick} variant="secondary">
          <PlaneSendIcon />
          <FormattedMessage
            defaultMessage="Post Job to {channel}"
            values={{ channel: channels[0].name }}
          />
        </Button>
      ) : (
        <>
          <div className="flex gap-1">
            <Typography variant="subtitle1">
              <FormattedMessage defaultMessage="Channels:" />
            </Typography>
            <Typography variant="subtitle2">
              {channels.map(channel => channel.name).join(', ')}
            </Typography>
          </div>
          <Button
            onClick={handleMultipleChannelsPostJobClick}
            variant="secondary"
          >
            <PlaneSendIcon />
            <FormattedMessage defaultMessage="Post Job to channels" />
          </Button>
          <SelectChannelsDialog />
        </>
      )}
    </Card>
  );
}
